import React from "react";
import { Alert, Box, CircularProgress, Paper, Typography } from "@mui/material";
import { Block } from "@mui/icons-material";
import { useModulePermissions } from "../hooks/useModulePermissions";
import { useAnalysisScope } from "../components/analysis/useAnalysisScope";
import StudentProgressView from "../components/analysis/StudentProgressView";
import SubjectPerformanceView from "../components/analysis/SubjectPerformanceView";
import ClassPerformanceView from "../components/analysis/ClassPerformanceView";

/** The three analysis pages, in route order (`tabIndex`). Titles match the sidebar. */
const PAGES = [
  { permission: "student_progress", title: "Student Progress", description: "How one student is doing across the session's exams", View: StudentProgressView },
  { permission: "subject_performance", title: "Subject Performance", description: "How each subject is going for a class across exams", View: SubjectPerformanceView },
  { permission: "class_performance", title: "Class Performance", description: "How a class did in one exam, student by student", View: ClassPerformanceView },
] as const;

interface AnalysisPageProps {
  tabIndex?: number;
}

const AnalysisPage: React.FC<AnalysisPageProps> = ({ tabIndex = 0 }) => {
  const { canRead } = useModulePermissions();
  const { scope, error } = useAnalysisScope();
  const page = PAGES[tabIndex] ?? PAGES[0];
  const { View } = page;

  if (!canRead(page.permission)) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="70vh">
        <Paper variant="outlined" sx={{ p: 5, textAlign: "center", maxWidth: 420, borderRadius: 2 }}>
          <Block sx={{ fontSize: 56, color: "text.disabled", mb: 2 }} />
          <Typography variant="h6" fontWeight={500} gutterBottom>Access Denied</Typography>
          <Typography variant="body2" color="text.secondary">You don't have permission to view {page.title}.</Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 2.5 }}>
        <Typography variant="h4" fontWeight={600}>{page.title}</Typography>
        <Typography variant="body2" color="text.secondary">{page.description}</Typography>
      </Box>
      {error ? (
        <Alert severity="error">{error}</Alert>
      ) : !scope ? (
        <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
      ) : (
        <View scope={scope} />
      )}
    </Box>
  );
};

export default AnalysisPage;
